import { Link, type LinkProps } from '@tanstack/react-router'
import { X, type LucideIcon } from 'lucide-react'
import { Fragment, type ComponentProps, type ReactNode } from 'react'

import type { CatalogueKind } from '@brickup/shared/enums'
import { COMMON } from '@brickup/shared/locales/de/common'
import {
  Button,
  cn,
  DashedAddButton,
  FOCUS_RING,
  LoadingIndicator,
} from '@/components/base'
import { EmptyState } from '@/components/EmptyState'
import { catalogueLink } from '@/components/Internal/Catalogue/catalogue-filters'
import { CatalogueGlyph } from '@/components/Internal/Catalogue/CatalogueGlyph'
import { PRESSABLE_ROW_CLASS, PressableRow } from '@/components/PressableRow'
import { SortableItem, SortableList } from '@/components/SortableItem'

/**
 * One row of a list of links — a product a measure is carried out with, a
 * measure offered under a question, a group a question hangs in.
 *
 * All three are the same row: what the link points at on the left, whatever
 * that particular link carries in the middle, and the button that cuts it on
 * the right. Written out per screen they drifted in row height, in whether the
 * name was a link at all, and in whether the cut was an `X` or a bin.
 *
 * The row is a link where the other end has a pane of its own, a button where
 * pressing it only moves a selection, and plain text where it has neither.
 */
export function LinkRow({
  handle,
  leading,
  title,
  subtitle,
  to,
  onPress,
  onRemove,
  removeLabel = COMMON.remove,
  removeIcon = X,
  children,
}: {
  /** The grip, where the list is sortable — handed down by `SortableItem`. */
  handle?: ReactNode
  /** A swatch, a thumbnail, a glyph. */
  leading?: ReactNode
  title: ReactNode
  /** The line under the title: a price, the chapter a group sits in. */
  subtitle?: ReactNode
  /** Where the other end lives, for something with a pane of its own. */
  to?: LinkProps
  /** For an end that is not a route — a row of this same screen. */
  onPress?: () => void
  onRemove?: () => void
  removeLabel?: string
  /** What the button on the right does, where it is not cutting a link: a bin
   *  for a row whose press destroys the thing rather than the link to it. */
  removeIcon?: LucideIcon
  /** What this link carries: a surcharge, a switch, a second action. */
  children?: ReactNode
}) {
  const body = (
    <>
      {leading}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[13px] text-foreground">{title}</span>
        {subtitle ? (
          <span className="truncate text-[11px] text-faint">{subtitle}</span>
        ) : null}
      </span>
    </>
  )

  const reach = cn(
    PRESSABLE_ROW_CLASS,
    'min-w-0 flex-1 gap-2 rounded-md px-1.5 py-1 text-left transition-colors',
  )
  const pressable = cn(reach, 'hover:bg-line-soft', FOCUS_RING)

  return (
    <div className="flex flex-row items-center gap-2">
      {handle}
      {to ? (
        <Link {...to} className={pressable}>
          {body}
        </Link>
      ) : onPress ? (
        <PressableRow onPress={onPress} className={pressable}>
          {body}
        </PressableRow>
      ) : (
        <span className={cn(reach, 'cursor-default')}>{body}</span>
      )}

      {children}

      {onRemove ? (
        <Button
          size="icon"
          variant="ghost"
          // Quiet at rest: a list of ten links would otherwise be a column of
          // red, and none of them is the dangerous one until it is pressed.
          className="text-faint hover:text-destructive"
          tooltip={removeLabel}
          icon={removeIcon}
          onClick={onRemove}
        />
      ) : null}
    </div>
  )
}

/** One row, given either as `LinkRow` takes it or as the catalogue entry it
 *  points at. */
export type LinkRowListRow = ComponentProps<typeof LinkRow> & {
  key: string | number
  /** The branch the row points into. Gives it that branch's mark, and — with
   *  `id` — the way into its pane, so no caller composes either by hand. */
  kind?: CatalogueKind
  id?: number | null
}

/**
 * Links one under the other, or what stands in their place while there are
 * none.
 *
 * Every list of what an entry is linked to is this one: the measures that
 * charge a Preisbaustein, the Preisgruppen it belongs to, what a Preisgruppe
 * is made of, the questions a configurator node hangs in, a dashboard's short
 * list of places to go. They were drawn as pills on one screen and rows on the
 * next, and the list an operator could cut from looked nothing like the one
 * they could not — a screen to re-learn per branch, and the same list written
 * out five times.
 *
 * Read as three states, not two: while the query is in flight an empty list is
 * "not known yet", and both a bare box and a "nothing here" line would be
 * claims the answer has not arrived to support.
 *
 * What the list can do is what it is given. A list of what uses an entry is
 * something the catalogue reports and takes neither `onRemove` nor `onAdd`; a
 * membership takes both; one whose order is part of what it says takes
 * `onReorder` as well.
 */
export function LinkRowList({
  loading = false,
  rows,
  emptyTitle,
  onRemove,
  removeLabel,
  onReorder,
  onAdd,
  addLabel = COMMON.add,
}: {
  loading?: boolean
  rows: LinkRowListRow[]
  /** What stands in the list's place while it has no rows, as `DataTable`
   *  names it. */
  emptyTitle: string
  /** Switched on by passing it: not every list is one a reader may cut. A row
   *  stating its own `onRemove` keeps that one. */
  onRemove?: (row: LinkRowListRow) => void
  removeLabel?: string
  /** Switched on by passing it, for a list whose order is part of what it
   *  says — a Preisgruppe charges its members in the order it lists them. */
  onReorder?: (rows: LinkRowListRow[]) => void
  /** Switched on by passing it: a list of what uses an entry is a fact, not a
   *  set somebody keeps. */
  onAdd?: () => void
  addLabel?: string
}) {
  if (loading) return <LoadingIndicator layout="inline" />

  /** The row as `LinkRow` takes it: what the caller stated, and the branch's
   *  own mark and route where it stated only which entry this is. */
  const draw = ({ key: _key, kind, id, ...row }: LinkRowListRow) => (
    <LinkRow
      removeLabel={removeLabel}
      {...row}
      leading={
        row.leading ??
        (kind ? <CatalogueGlyph kind={kind} entry={{}} size={16} /> : undefined)
      }
      to={row.to ?? (kind && id != null ? catalogueLink(kind, id) : undefined)}
    />
  )

  // `SortableItem` rather than `SortableListRow`: that one brings a row of its
  // own, and a second padding around `LinkRow` would set a draggable list
  // wider than the lists beside it — which is the one thing this component
  // exists to prevent.
  const list = (
    <div className="flex flex-col gap-1">
      {rows.map(row => {
        const drawn = (handle?: ReactNode) =>
          draw({
            ...row,
            handle,
            onRemove: row.onRemove ?? (onRemove && (() => onRemove(row))),
          })
        return onReorder ? (
          <SortableItem key={row.key} id={row.key} gripSize={13}>
            {({ handle }) => drawn(handle)}
          </SortableItem>
        ) : (
          <Fragment key={row.key}>{drawn()}</Fragment>
        )
      })}
    </div>
  )

  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 ? (
        <EmptyState layout="inline" title={emptyTitle} />
      ) : null}

      {rows.length > 0 && onReorder ? (
        <SortableList items={rows} getId={row => row.key} onReorder={onReorder}>
          {list}
        </SortableList>
      ) : null}

      {rows.length > 0 && !onReorder ? list : null}

      {onAdd ? <DashedAddButton label={addLabel} onClick={onAdd} /> : null}
    </div>
  )
}
